import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type OrgSubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | string;

export interface OrgSubscriptionState {
  has_subscription: boolean;
  status: OrgSubscriptionStatus | null;
  product: string | null;
  billing_cycle: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  locked_at: string | null;
  is_trialing: boolean;
  is_locked: boolean;
  trial_days_remaining: number;
  is_org_super_admin: boolean;
}

/**
 * Reads the canonical subscription state for the given org via the
 * `public.get_org_subscription_state` RPC. The RPC is owned by the
 * origin (hibane-desk) project and is not present in our generated
 * types here — call site casts through `unknown`.
 */
export function useOrgSubscriptionState(orgId: string | null) {
  const query = useQuery({
    queryKey: ["org-subscription-state", orgId],
    enabled: !!orgId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<OrgSubscriptionState | null> => {
      if (!orgId) return null;
      try {
        const rpc = (supabase.rpc as unknown as (
          name: string,
          args: Record<string, unknown>
        ) => Promise<{ data: unknown; error: unknown }>);
        const { data, error } = await rpc("get_org_subscription_state", {
          _org_id: orgId,
        });
        if (error) return null;
        if (!data || typeof data !== "object") return null;
        const d = data as Record<string, unknown>;
        if (typeof d.error === "string") return null;
        return {
          has_subscription: Boolean(d.has_subscription),
          status: (d.status as string | null) ?? null,
          product: (d.product as string | null) ?? null,
          billing_cycle: (d.billing_cycle as string | null) ?? null,
          trial_ends_at: (d.trial_ends_at as string | null) ?? null,
          current_period_end: (d.current_period_end as string | null) ?? null,
          cancel_at_period_end: Boolean(d.cancel_at_period_end),
          locked_at: (d.locked_at as string | null) ?? null,
          is_trialing: Boolean(d.is_trialing),
          is_locked: Boolean(d.is_locked),
          trial_days_remaining: Number(d.trial_days_remaining ?? 0),
          is_org_super_admin: Boolean(d.is_org_super_admin),
        };
      } catch {
        return null;
      }
    },
  });

  return {
    state: query.data ?? null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export default useOrgSubscriptionState;